import type { ProductDto } from '@repo/api/web/models'
import { withoutTrailingSlash } from 'ufo'
import { toValue, type MaybeRefOrGetter, type Ref } from 'vue'

import { OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH } from '../services'
import { breadcrumbJsonLd, jsonLdScript, productJsonLd, type ProductJsonLdReviews } from '../utils/jsonLd'

/**
 * SEO-мета карточки товара (SSR): уникальные title/description,
 * Open Graph/Twitter и JSON-LD (Product + BreadcrumbList) из данных товара.
 * `reviews` — рейтинг и первые отзывы для `aggregateRating`/`review` (модуль Reviews,
 * `reviewsSeoOpts`); без него или при пустом рейтинге разметки рейтинга нет.
 * Дефолты уровня витрины — в seoRoot-плагине.
 */
export const useProductDetailsSeo = (
  product: Ref<ProductDto | null | undefined>,
  reviews?: MaybeRefOrGetter<ProductJsonLdReviews | null | undefined>,
) => {
  const { service: seo } = useSeo()
  const { routing } = useProducts()
  const route = useRoute()
  const url = useRequestURL()

  // og-картинку и JSON-LD считаем жадно (watchEffect выполняется в
  // setup-контексте): геттеры useSeoMeta/useHead вычисляются при рендере head
  // вне setup, а image-провайдер внутри вызывает useRuntimeConfig — там это
  // падает «composable ... outside setup»
  const ogImage = ref<string | undefined>()
  watchEffect(() => {
    ogImage.value = product.value
      ? seo.productOgImage(product.value)
      : undefined
  })

  const jsonLdScripts = ref<ReturnType<typeof jsonLdScript>[]>([])
  watchEffect(() => {
    if (!product.value) {
      jsonLdScripts.value = []
      return
    }

    const pageUrl = url.origin + withoutTrailingSlash(route.path)
    const category = product.value.categories?.[0]

    jsonLdScripts.value = [
      jsonLdScript(productJsonLd(product.value, {
        url: pageUrl,
        image: ogImage.value,
        description: seo.productDescription(product.value),
        ...toValue(reviews),
      })),
      jsonLdScript(breadcrumbJsonLd([
        { name: 'Главная', url: `${url.origin}/` },
        ...(category?.displayName
          ? [{
              name: category.displayName,
              url: `${url.origin}${routing.getCatalog(category).route}`,
            }]
          : []),
        { name: seo.productTitle(product.value) },
      ])),
    ]
  })

  useSeoMeta({
    title: () => (product.value ? seo.productTitle(product.value) : undefined),
    description: () =>
      product.value ? seo.productDescription(product.value) : undefined,

    ogTitle: () =>
      product.value ? seo.productTitle(product.value) : undefined,
    ogDescription: () =>
      product.value ? seo.productDescription(product.value) : undefined,
    ogImage: () => ogImage.value,
    ogImageWidth: OG_IMAGE_WIDTH,
    ogImageHeight: OG_IMAGE_HEIGHT,

    twitterCard: () => (ogImage.value ? 'summary_large_image' : 'summary'),
    twitterTitle: () =>
      product.value ? seo.productTitle(product.value) : undefined,
    twitterDescription: () =>
      product.value ? seo.productDescription(product.value) : undefined,
    twitterImage: () => ogImage.value,
  })

  useHead(computed(() => ({
    // og:type=product отсутствует в типизации useSeoMeta — задаём напрямую
    // (перекрывает дефолтный og:type=website из seoRoot-плагина)
    meta: [{ property: 'og:type', content: 'product' }],
    script: jsonLdScripts.value,
  })))
}
